/**
 * Nupkeet Cart Engine - Unified & Robust
 * Handles: Quantity updates, removals, drawer refresh, shipping calculator
 */

(function () {
    const DEBUG = false;
    function log(...args) { if (DEBUG) console.log('[Nupkeet Cart]', ...args); }

    const NpkCartEngine = {
        isUpdating: false,

        init() {
            log('Engine Initializing...');
            this.bindEvents();
            this.initShippingCalculator();
            log('Engine Ready.');
        },

        bindEvents() {
            // 1. QUANTITY BUTTONS & INPUTS (Global delegation)
            document.addEventListener('click', (e) => {
                const btn = e.target.closest('.js-qty-btn');
                if (!btn) return;

                e.preventDefault();
                e.stopPropagation();

                if (this.isUpdating) return;

                const key = btn.dataset.itemKey;
                const action = btn.dataset.action;

                const wrapper = btn.closest('.cart-item__qty-selector, .quantity-wrapper');
                const input = wrapper?.querySelector('.quantity-input');
                const display = wrapper?.querySelector('.cart-item__qty-display');

                let currentQty = 0;
                if (input) currentQty = parseInt(input.value) || 0;
                else if (display) currentQty = parseInt(display.textContent) || 0;

                const newQty = action === 'plus' ? currentQty + 1 : Math.max(0, currentQty - 1);
                this.updateCart(key, newQty);
            }, true);

            // Handle manual input changes
            document.addEventListener('change', (e) => {
                const input = e.target.closest('.quantity-input');
                if (!input) return;

                const key = input.dataset.id || input.dataset.itemKey;
                const newQty = Math.max(0, parseInt(input.value) || 0);
                this.updateCart(key, newQty);
            });

            // 2. REMOVE BUTTONS
            document.addEventListener('click', (e) => {
                const remove = e.target.closest('[data-remove-item], .cart-item-remove');
                if (!remove) return;

                e.preventDefault();
                e.stopPropagation();

                const key = remove.dataset.id || remove.dataset.itemKey || remove.href?.split('id=')[1]?.split('&')[0];
                if (key) this.updateCart(key, 0);
            }, true);

            // 3. DRAWER TRIGGER
            document.addEventListener('click', (e) => {
                if (e.target.closest('.js-cart-trigger')) {
                    e.preventDefault();
                    this.openDrawer();
                }
                if (e.target.closest('.cart-drawer__close') || e.target.closest('#CartDrawerOverlay')) {
                    this.closeDrawer();
                }
            });

            // 4. ADD TO CART INTERCEPT
            document.addEventListener('submit', (e) => {
                const form = e.target.closest('form[action="/cart/add"]');
                if (!form) return;

                e.preventDefault();
                this.addItemFromForm(form);
            });
        },

        async updateCart(key, quantity) {
            if (this.isUpdating) return;
            this.isUpdating = true;
            document.body.classList.add('npk-cart-loading');

            try {
                const root = (window.routes?.root || '/').replace(/\/$/, '');
                const isCartPage = window.location.pathname.includes('/cart');
                const sectionsToRender = isCartPage ? 'main-cart,cart-drawer' : 'cart-drawer';

                const response = await fetch(`${root}/cart/change.js?sections=${sectionsToRender}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: key, quantity: quantity })
                });

                if (!response.ok) throw new Error('Cart update failed');
                const data = await response.json();

                this.renderSections(data.sections);
                this.updateCartCounts(data.item_count);

                if (isCartPage && data.item_count === 0) {
                    window.location.reload();
                }
            } catch (err) {
                console.error('[Nupkeet Cart] Update failed', err);
            } finally {
                this.isUpdating = false;
                document.body.classList.remove('npk-cart-loading');
            }
        },

        renderSections(sections) {
            if (!sections) return;

            Object.entries(sections).forEach(([id, html]) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                let selectors = [];
                if (id.includes('cart-drawer')) {
                    selectors = ['.cart-drawer__inner'];
                } else if (id.includes('main-cart') || id.includes('main')) {
                    selectors = ['.cart-grid', '.cart-header', '.cart-shipping-bar', '.cart-totals', '.cart-sticky-checkout-mobile'];
                }

                selectors.forEach(sel => {
                    const newEl = doc.querySelector(sel);
                    const currentEl = document.querySelector(sel);
                    if (newEl && currentEl) {
                        currentEl.innerHTML = newEl.innerHTML;
                        if (newEl.className) currentEl.className = newEl.className;
                    }
                });
            });
        },

        updateCartCounts(count) {
            const updateCount = (newCount) => {
                document.querySelectorAll('.js-cart-count').forEach(el => {
                    el.textContent = newCount;
                    el.classList.toggle('js-hidden', newCount === 0);
                });
            };

            if (typeof count !== 'undefined') {
                updateCount(count);
            } else {
                fetch('/cart.js').then(res => res.json()).then(cart => updateCount(cart.item_count));
            }
        },

        openDrawer() {
            const drawer = document.getElementById('CartDrawer');
            if (drawer) {
                drawer.classList.add('is-open');
                document.body.style.overflow = 'hidden';
            }
        },

        closeDrawer() {
            const drawer = document.getElementById('CartDrawer');
            if (drawer) {
                drawer.classList.remove('is-open');
                document.body.style.overflow = '';
            }
        },

        async addItemFromForm(form) {
            const formData = new FormData(form);
            document.body.classList.add('npk-cart-loading');

            try {
                const root = (window.routes?.root || '/').replace(/\/$/, '');
                const response = await fetch(`${root}/cart/add.js?sections=cart-drawer`, {
                    method: 'POST',
                    body: formData
                });
                const data = await response.json();

                if (data.sections) this.renderSections(data.sections);
                this.updateCartCounts(data.item_count);

                if (window.quickView) window.quickView.close();

                this.openDrawer();
            } catch (err) {
                console.error('[Nupkeet Cart] Add failed', err);
            } finally {
                document.body.classList.remove('npk-cart-loading');
            }
        },

        initShippingCalculator() {
            // Updated to use delegation to survive AJAX updates
            document.addEventListener('change', (e) => {
                const countrySelect = e.target.closest('#AddressCountry');
                if (!countrySelect) return;

                const provinceSelect = document.getElementById('AddressProvince');
                const provinceContainer = document.getElementById('AddressProvinceContainer');
                if (!provinceSelect || !provinceContainer) return;

                const selectedOption = countrySelect.options[countrySelect.selectedIndex];
                const provincesJson = selectedOption.getAttribute('data-provinces');
                if (provincesJson) {
                    const provinces = JSON.parse(provincesJson);
                    if (provinces.length > 0) {
                        provinceSelect.innerHTML = '';
                        provinces.forEach(p => {
                            const opt = document.createElement('option');
                            opt.value = p[0]; opt.text = p[1];
                            provinceSelect.appendChild(opt);
                        });
                        provinceContainer.style.display = 'block';
                    } else {
                        provinceContainer.style.display = 'none';
                    }
                }
            });

            document.addEventListener('click', (e) => {
                const submitBtn = e.target.closest('.get-rates-btn');
                if (!submitBtn) return;

                const countrySelect = document.getElementById('AddressCountry');
                const provinceSelect = document.getElementById('AddressProvince');
                const zipInput = document.getElementById('AddressZip');
                const responseContainer = document.getElementById('wrapper-response');

                submitBtn.textContent = '...';
                const params = new URLSearchParams();
                params.append('shipping_address[zip]', zipInput?.value || '');
                params.append('shipping_address[country]', countrySelect?.value || '');
                params.append('shipping_address[province]', provinceSelect?.value || '');

                fetch(`/cart/shipping_rates.json?${params.toString()}`)
                    .then(res => res.json())
                    .then(res => {
                        submitBtn.textContent = 'Calcola';
                        if (res.shipping_rates) {
                            responseContainer.innerHTML = res.shipping_rates.map(r => `<div class="shipping-rate">${r.name}: ${r.price}</div>`).join('');
                        } else {
                            responseContainer.innerHTML = 'Impossibile calcolare le tariffe.';
                        }
                    })
                    .catch(err => {
                        console.error('Shipping calc error', err);
                        submitBtn.textContent = 'Calcola';
                    });
            });
        }
    };

    // Launch on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => NpkCartEngine.init());
    } else {
        NpkCartEngine.init();
    }

    // Expose globally for manual control if needed
    window.NpkCartEngine = NpkCartEngine;
})();
